import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  catchError,
  defer,
  EMPTY,
  finalize,
  from,
  map,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { ApiResponse, ConversionData } from '../../../types/commons';
import { HttpStatus } from '../../utils/http-status.util';
import { normalizeApiErrors } from '../../utils/api-errors.util';
import { triggerBlobDownload } from '../../utils/trigger-blob-download.util';
import { deriveXbrlDownloadNames } from '../../utils/xbrl-download-naming.util';
import { zipSingleFile } from '../../utils/zip-single-file.util';
import { RuntimeConfigService } from './runtime-config.service';

/**
 * A download step (generate, or the SAS-URL read) the backend refused.
 *
 * Carries the backend `errorCode` so the caller can say "no access" or "no
 * longer exists" instead of a generic failure. `errorCode` stays undefined when
 * the backend sent none (an ordinary fault); the message is console-only.
 */
export class XbrlDownloadApiError extends Error {
  constructor(message: string, readonly errorCode?: string) {
    super(message);
    this.name = 'XbrlDownloadApiError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class DownloadXbrlService {
  private readonly config = inject(RuntimeConfigService);
  private readonly http = inject(HttpClient);
  private readonly API_URL = `${this.config.apiUrl()}/${this.config.apiVersion()}/mbrs/download-xbrl`;
  private readonly API_RETRIEVE_XBRL_FILE_URL = `${this.config.apiUrl()}/mbrs/file-url`;

  private downloadLoadingMap = new Map<string, WritableSignal<boolean>>();
  private sasCache = new Map<string, { url: string; expiresAt: number }>();

  /** Per-row in-flight flag. Bound by the listing template to disable the menu item. */
  getDownloadSignal(id: string): WritableSignal<boolean> {
    if (!this.downloadLoadingMap.has(id)) {
      this.downloadLoadingMap.set(id, signal(false));
    }
    return this.downloadLoadingMap.get(id)!;
  }

  /** The blob storage path key both `getSasUrl`'s cache and the failure-path cache-bust share. */
  private buildFilePath(id: string): string {
    let env: string = environment.env;
    env = env ? env.charAt(0).toUpperCase() + env.slice(1) : env;
    return `${env}/XbrlFiles/${id}.xml`;
  }

  getSasUrl(item: ConversionData): Observable<string> {
    const { id, xbrlFileName, fileName } = item;
    const filePath = this.buildFilePath(id);

    // `xbrlFileName` is optional (undefined on pre-rollout / older cached rows).
    // Fall back to the source `fileName` — this mirrors the backend's own
    // "Filing Info not yet available → source FileName" rule. Send the bare
    // name WITHOUT `.xml`: the backend's BlobStorageService appends the
    // extension exactly once when it builds the SAS. That content-disposition
    // is now irrelevant to us (we read the bytes and name the file ourselves),
    // but the field is `[Required]` server-side.
    const downloadName = xbrlFileName || fileName;

    const now = Date.now();
    const cached = this.sasCache.get(filePath);
    if (cached && cached.expiresAt > now) {
      return of(cached.url);
    }

    return this.http
      .post<ApiResponse<string>>(
        this.API_RETRIEVE_XBRL_FILE_URL,
        { FilePath: filePath, FileName: downloadName },
        { withCredentials: true }
      )
      .pipe(
        catchError(this.handleError),
        switchMap((response) => {
          const url = response?.data as string | undefined;
          if (!response?.status || !url) {
            return throwError(() => new XbrlDownloadApiError(this.describe('sas', response), response?.errorCode));
          }
          // Server SAS validity is 5 minutes; cache for 3 so a cached URL can
          // never outlive it.
          this.sasCache.set(filePath, { url, expiresAt: now + 3 * 60 * 1000 });
          return of(url);
        })
      );
  }

  downloadXbrlApi(id: string): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse<string>>(
        this.API_URL,
        { conversionId: id },
        { withCredentials: true }
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Regenerates the XBRL instance, fetches its bytes, zips them in the browser,
   * and saves the archive.
   *
   * HARD FAIL BY DESIGN — there is no fallback to the old `.xml` navigation.
   * Callers subscribe and surface the error; see `XbrlFilesComponent`.
   *
   * `defer` matters: `deriveXbrlDownloadNames` throws synchronously, and without
   * `defer` that throw would escape at call time instead of reaching the
   * subscriber's `error` handler.
   */
  downloadXbrlFile(item: ConversionData): Observable<void> {
    return defer(() => {
      const loading = this.getDownloadSignal(item.id);
      // Guard an impatient double-click: a second click would fire another
      // regenerate + blob upload for the same row.
      if (loading())
        return EMPTY;

      // Derived BEFORE any network call so a row with no usable name fails
      // instantly rather than after a full regeneration.
      const names = deriveXbrlDownloadNames(item.xbrlFileName, item.fileName);
      loading.set(true);

      return this.downloadXbrlApi(item.id).pipe(
        switchMap((response) =>
          response?.status
            ? this.getSasUrl(item)
            : throwError(() => new XbrlDownloadApiError(this.describe('generate', response), response?.errorCode))
        ),
        switchMap((sasUrl) =>
          from(this.fetchBlobBytes(sasUrl)).pipe(
            catchError((error) => {
              // A failed fetch must not leave the SAS cached — an immediate
              // retry would otherwise reuse the URL that just failed for up
              // to 3 minutes (see the cache comment in `getSasUrl`).
              this.sasCache.delete(this.buildFilePath(item.id));
              // The one failure this design expects is a missing Azure
              // Storage CORS rule; the ORIGIN (never the full SAS URL/query —
              // that carries the signature, a credential) is the operator's
              // first diagnostic question.
              console.warn(
                '[xbrl-download] blob fetch failed; check Azure Storage CORS for origin',
                new URL(sasUrl).origin,
                error
              );
              return throwError(() => error);
            })
          )
        ),
        switchMap((bytes) => from(zipSingleFile(names.xmlName, bytes))),
        map((blob) => {
          triggerBlobDownload(blob, names.zipName);
        }),
        finalize(() => loading.set(false))
      );
    });
  }

  /**
   * Reads the blob via NATIVE `fetch`, never `HttpClient`.
   *
   * Two interceptors make `HttpClient` unsafe for a cross-origin blob URL:
   * `csrfInterceptor` would attach `X-XSRF-TOKEN`, forcing a CORS preflight the
   * storage account will not answer; and `session-expiry.interceptor` matches on
   * PATHNAME ONLY, so an Azure auth failure could reach the forced-logout path
   * and sign the user out.
   *
   * `credentials: 'omit'` is deliberate — SAS authorisation lives in the query
   * string, and sending cookies would additionally require
   * `Access-Control-Allow-Credentials` on the storage account.
   */
  private async fetchBlobBytes(sasUrl: string): Promise<Uint8Array> {
    const response = await fetch(sasUrl, { credentials: 'omit', cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Blob fetch failed with HTTP ${response.status}`);
    }
    return new Uint8Array(await response.arrayBuffer());
  }

  /** Console-facing description; never shown to the user (see the toast copy). */
  private describe(stage: string, response: ApiResponse | undefined): string {
    const detail = normalizeApiErrors(response?.errors).join('; ');
    return `XBRL download failed at stage "${stage}": ${detail || 'no error detail returned'}`;
  }

  private handleError(error: HttpErrorResponse): Observable<ApiResponse> {
    if (error.error && error.error.errors && error.error.errors.length > 0) {
      return of(error.error);
    }

    // Not a passthrough, so `errorCode` must be copied by hand or a code-only
    // body (no `errors` text) would lose it. Omitted when absent or empty.
    const errorCode = error.error?.errorCode;
    return of({
      ...(typeof errorCode === 'string' && errorCode.length > 0 ? { errorCode } : {}),
      status: false,
      statusCode: error.status || HttpStatus.InternalServerError,
      data: {},
      errors: [error.message || 'Unknown error occurred'],
    });
  }
}
